/**
 * Admin-only company letterhead branding: logo + signature upload/preview.
 */
import { useEffect, useRef, useState } from 'react';
import { Building2, Camera, PenLine } from 'lucide-react';
import { ApiRequestError } from '../../../lib/api.js';
import { Button } from '../../../shell/Button.js';
import { Input } from '../../../shell/Field.js';
import { toast } from '../../../shell/Toast.js';
import {
  useCompanyBranding,
  useUpdateCompanyBranding,
  useUploadBrandingLogo,
  useUploadBrandingSignature,
} from '../brandingApi.js';

export function BrandingCard() {
  const { data, isLoading } = useCompanyBranding(true);
  const updateBranding = useUpdateCompanyBranding();
  const uploadLogo = useUploadBrandingLogo();
  const uploadSignature = useUploadBrandingSignature();
  const logoRef = useRef<HTMLInputElement>(null);
  const signatureRef = useRef<HTMLInputElement>(null);
  const [companyName, setCompanyName] = useState('');
  const [addressText, setAddressText] = useState('');

  useEffect(() => {
    if (!data) return;
    setCompanyName(data.companyName ?? '');
    setAddressText((data.addressLines ?? []).join('\n'));
  }, [data?.updatedAt, data?.companyName, data?.addressLines?.join('\n')]);

  const uploadImage = async (
    file: File | undefined,
    kind: 'logo' | 'signature',
  ) => {
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      toast('error', 'Please choose an image file.');
      return;
    }
    const formData = new FormData();
    formData.append('file', file);
    try {
      if (kind === 'logo') await uploadLogo.mutateAsync(formData);
      else await uploadSignature.mutateAsync(formData);
      toast('success', kind === 'logo' ? 'Logo updated.' : 'Signature updated.');
    } catch (err) {
      const msg = err instanceof ApiRequestError ? err.body.message : `Failed to upload ${kind}.`;
      toast('error', msg);
    }
  };

  const saveText = async () => {
    const addressLines = addressText
      .split('\n')
      .map((line) => line.trim())
      .filter(Boolean)
      .slice(0, 8);
    try {
      await updateBranding.mutateAsync({
        companyName: companyName.trim() || 'Vibe Wireless',
        addressLines,
      });
      toast('success', 'Company details saved.');
    } catch (err) {
      const msg = err instanceof ApiRequestError ? err.body.message : 'Failed to save branding.';
      toast('error', msg);
    }
  };

  return (
    <section className="rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-5">
      <div className="mb-4 flex items-center gap-2">
        <Building2 className="h-4 w-4 text-slate-400" />
        <h2 className="text-sm font-semibold text-slate-900 dark:text-slate-100">Company Letterhead</h2>
      </div>
      <p className="mb-4 text-sm text-slate-500 dark:text-slate-400">
        Logo and signature appear on generated action PDFs. PNG or JPEG recommended.
      </p>

      {isLoading && !data ? (
        <p className="text-sm text-slate-500">Loading branding…</p>
      ) : (
        <div className="space-y-5">
          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <p className="mb-2 text-xs font-medium uppercase tracking-wide text-slate-500">Logo</p>
              <div className="mb-3 flex h-24 items-center justify-center rounded-lg border border-dashed border-slate-300 dark:border-slate-700 bg-slate-50 dark:bg-slate-950/40 overflow-hidden">
                {data?.logoUrl ? (
                  <img src={data.logoUrl} alt="Company logo" className="max-h-20 max-w-full object-contain" />
                ) : (
                  <span className="text-xs text-slate-400">No logo uploaded</span>
                )}
              </div>
              <input
                ref={logoRef}
                type="file"
                accept="image/png,image/jpeg,image/webp,image/gif"
                className="hidden"
                onChange={async (e) => {
                  await uploadImage(e.target.files?.[0], 'logo');
                  if (logoRef.current) logoRef.current.value = '';
                }}
              />
              <Button
                variant="secondary"
                size="sm"
                onClick={() => logoRef.current?.click()}
                loading={uploadLogo.isPending}
              >
                <Camera className="h-4 w-4" /> Upload logo
              </Button>
            </div>

            <div>
              <p className="mb-2 text-xs font-medium uppercase tracking-wide text-slate-500">Signature</p>
              <div className="mb-3 flex h-24 items-center justify-center rounded-lg border border-dashed border-slate-300 dark:border-slate-700 bg-slate-50 dark:bg-slate-950/40 overflow-hidden">
                {data?.signatureUrl ? (
                  <img src={data.signatureUrl} alt="Company signature" className="max-h-20 max-w-full object-contain" />
                ) : (
                  <span className="text-xs text-slate-400">No signature uploaded</span>
                )}
              </div>
              <input
                ref={signatureRef}
                type="file"
                accept="image/png,image/jpeg,image/webp,image/gif"
                className="hidden"
                onChange={async (e) => {
                  await uploadImage(e.target.files?.[0], 'signature');
                  if (signatureRef.current) signatureRef.current.value = '';
                }}
              />
              <Button
                variant="secondary"
                size="sm"
                onClick={() => signatureRef.current?.click()}
                loading={uploadSignature.isPending}
              >
                <PenLine className="h-4 w-4" /> Upload signature
              </Button>
            </div>
          </div>

          <div className="space-y-3">
            <Input
              label="Company name"
              value={companyName}
              onChange={(e) => setCompanyName(e.target.value)}
            />
            <label className="block">
              <span className="mb-1 block text-sm font-medium text-slate-700 dark:text-slate-300">
                Address lines
              </span>
              <textarea
                value={addressText}
                onChange={(e) => setAddressText(e.target.value)}
                rows={3}
                placeholder={'One line per row\nCity, ST 00000'}
                className="w-full rounded-lg border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-950 px-3 py-2 text-sm text-slate-900 dark:text-slate-100"
              />
            </label>
            <Button onClick={saveText} loading={updateBranding.isPending}>
              Save company details
            </Button>
          </div>
        </div>
      )}
    </section>
  );
}
